import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulCurrentActiveEntries, ContentfulNav } from '@jbhi-fi/apis/src/contentful/types';

export type UseContentfulActiveNavsProps = {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
};

type CurrentActiveNavs = Pick<
  Exclude<ContentfulCurrentActiveEntries['fields'], undefined>,
  'headerNav' | 'footerNav' | 'footerPayments' | 'footerLegals' | 'footerSocials'
>;

export type UseContentfulActiveNavsResult = {
  isLoading?: boolean;
} & Partial<CurrentActiveNavs>;

export const useContentfulActiveNavs = ({
  contentfulThemeApiClient: contentfulThemeApiClientProp,
}: UseContentfulActiveNavsProps): UseContentfulActiveNavsResult => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext } = usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulActiveNavs must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }

  const { isLoading: isActiveEntryLoading, data: activeEntryData } = useQuery({
    enabled: Boolean(contentfulThemeApiClient),
    queryKey: ['useContentfulActiveNavs', 'currentActiveEntry'],
    refetchOnWindowFocus: false,
    queryFn: () =>
      contentfulThemeApiClient?.getEntries({ content_type: 'currentActiveHomepage', include: '0', cache: 'medium' }),
  });
  const currentActiveEntry = activeEntryData?.data?.items?.[0] as ContentfulCurrentActiveEntries | undefined;

  const { headerNav, footerNav, footerPayments, footerLegals, footerSocials } = currentActiveEntry?.fields ?? {};
  const navEntriesIds = [
    headerNav?.sys?.id,
    footerNav?.sys?.id,
    footerPayments?.sys?.id,
    footerLegals?.sys?.id,
    footerSocials?.sys?.id,
  ].filter(Boolean);

  const { isLoading: isNavEntriesLoading, data: navEntriesData } = useQuery({
    enabled: Boolean(contentfulThemeApiClient && navEntriesIds.length),
    queryKey: ['useContentfulActiveNavs', 'navEntries', navEntriesIds],
    refetchOnWindowFocus: false,
    queryFn: () =>
      contentfulThemeApiClient?.getEntries({
        content_type: 'nav',
        include: '6',
        'sys.id[in]': navEntriesIds.join(','),
        cache: 'medium',
      }),
  });

  const items = (navEntriesData?.data?.items ?? []) as Array<ContentfulNav>;

  return {
    isLoading: isActiveEntryLoading || isNavEntriesLoading,
    headerNav: items?.find(item => item.fields?.type === 'Header Nav'),
    footerNav: items?.find(item => item.fields?.type === 'Footer Nav'),
    footerSocials: items?.find(item => item.fields?.type === 'Socials'),
    footerLegals: items?.find(item => item.fields?.type === 'Footer Legals'),
    footerPayments: items?.find(item => item.fields?.type === 'Payment Providers'),
  };
};
